import { FiArrowLeft } from 'solid-icons/fi';
import type { Component, JSXElement, ParentComponent} from 'solid-js';
import { Show } from 'solid-js';
import type { DynamicProps } from 'solid-js/web';
import { Dynamic } from 'solid-js/web';
import type { HeadingElement, StyleSafeProps} from '@deltablue/ts-utils';
import { groomChildren } from '@deltablue/ts-utils';
import c from './Page.module.css';

type PageType = {
    // IDEA: add title and onClickBack slot, remove Page.Header.
    Donut: ParentComponent<{ tabs?: JSXElement; }>;
    // IDEA: add Page.Tab component, with an "active" prop
    /** CSS Class for Page.TabHeader link tabs. */
    tabLinkClass: string;
    /** CSS Class for Page.TabHeader active link tabs. */
    tabLinkClassActive: string;
    /** Header content. Only place directly inside Page.Donut. */
    Header: ParentComponent<{
        controls?: JSXElement;
        onClickBack?: () => void;
    }>;
    /** Only place directly inside Page.Donut */
    Title: <T extends HeadingElement | 'div'>(p: StyleSafeProps<DynamicProps<T>>) => JSXElement;
    /** Block for content with associated controls. Only place inside Page.Donut. */
    Content: Component<{
        children: {
            content: JSXElement;
            viewControls?: JSXElement;
            actionControls?: JSXElement;
            pageControls?: JSXElement;
        };
    }>;
    /** For spacing multiple sections inside Content>content. Only place inside Page.Content. */
    Section: ParentComponent;
};

export const Page: PageType = {
    Donut: p => (
        <div class={c.donut}>
            <Show when={p.tabs !== undefined}>
                <div class={c.tabs}>
                    {p.tabs}
                </div>
            </Show>
            <div class={c.page}>
                {p.children}
            </div>
        </div>
    ),
    tabLinkClass: c.tabLinkClass,
    tabLinkClassActive: c.tabLinkClassActive,
    Header: p => (
        <div class={c.header}>
            <div class={c.wrapper}>
                <Show when={p.onClickBack}>
                    <button class={c.back} onClick={p.onClickBack}>
                        <FiArrowLeft size='100%' />
                    </button>
                </Show>
                <div>
                    {p.children}
                </div>
            </div>
            <Show when={p.controls}>
                <div>
                    {p.controls}
                </div>
            </Show>
        </div>
    ),
    Title: p => (
        <Dynamic
            {...p}
            component={p.component}
            class={c.title}
        />
    ),
    Content: p => {
        const resolved = groomChildren(p.children);
        return (
            <div class={c.content}>
                <Show when={resolved().viewControls}>
                    {viewControls => (
                        <div class={c.viewControls}>
                            {viewControls()}
                        </div>
                    )}
                </Show>
                <div>
                    {resolved().content}
                </div>
                <Show when={resolved().pageControls}>
                    {pageControls => (
                        <div class={c.pageControls}>
                            {pageControls()}
                        </div>
                    )}
                </Show>
                <Show when={resolved().actionControls}>
                    {actionControls => (
                        <div class={c.actionControls}>
                            {actionControls()}
                        </div>
                    )}
                </Show>
            </div>
        );
    },
    Section: p => (
        <section class={c.section}>
            {p.children}
        </section>
    ),
};
